import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useProducts } from '../../hooks/useProducts';
import { useProductFilters } from '../../hooks/useProductFilters';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useBulkActions } from '../../hooks/useBulkActions';
import ProductsHeader from '../../components/products/ProductsHeader';
import ProductsFilters from '../../components/products/ProductsFilters';
import BulkActionsBar from '../../components/products/BulkActionsBar';
import ProductsTable from '../../components/products/ProductsTable';
import ProductsPagination from '../../components/products/ProductsPagination';
import AddProductModern from '../../components/admin/AddProductModern';
import EditProductModern from '../../components/admin/EditProductModern';
import DeleteProductConfirmation from '../../components/admin/DeleteProductConfirmation';
import BulkBrandAssignment from '../../components/admin/BulkBrandAssignment';

const AdminProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [paginationState, setPaginationState] = useState({
    currentPage: parseInt(searchParams.get('page')) || 1,
    itemsPerPage: parseInt(searchParams.get('perPage')) || 20
  });

  // Modals
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [addProductQuickMode, setAddProductQuickMode] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [showBulkBrandModal, setShowBulkBrandModal] = useState(false);

  // Custom hooks
  const { filters, updateFilters, clearFilters, activeFiltersCount } = useProductFilters();
  const { products, loading, pagination, globalStats, refetch, updateProduct, deleteProduct, addProduct } = useProducts(
    filters,
    paginationState
  );
  const inlineEdit = useInlineEdit(updateProduct);
  const bulkActions = useBulkActions(products, refetch);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get('/api/categories');
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  // Use global stats from API (not affected by pagination/filters)
  const stats = {
    total: globalStats.total || pagination.totalItems || 0,
    active: globalStats.active || 0,
    lowStock: globalStats.lowStock || 0
  };

  // Handlers
  const handlePageChange = (newPage) => {
    setPaginationState(prev => ({ ...prev, currentPage: newPage }));
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('page', newPage);
      return params;
    }, { replace: true });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setPaginationState({ currentPage: 1, itemsPerPage: newItemsPerPage });
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('page', '1');
      params.set('perPage', newItemsPerPage);
      return params;
    }, { replace: true });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      bulkActions.selectAll();
    } else {
      bulkActions.deselectAll();
    }
  };

  // Navigation
  const navigate = useNavigate();

  const handleEditProduct = (product) => {
    navigate(`/admin/products/edit/${product.id}`);
  };

  const handleProductUpdated = (updatedProduct) => {
    updateProduct(updatedProduct.id, updatedProduct);
    setShowEditProductModal(false);
    setEditingProduct(null);
  };

  const handleDeleteProduct = (product) => {
    setDeletingProduct(product);
    setShowDeleteConfirmModal(true);
  };

  const handleProductDeleted = (productId) => {
    deleteProduct(productId);
    setShowDeleteConfirmModal(false);
    setDeletingProduct(null);
  };

  const handleProductAdded = (newProduct) => {
    addProduct(newProduct);
    setShowAddProductModal(false);
  };

  const handleToggleFeatured = async (productId, currentFeaturedStatus) => {
    try {
      await axios.post(`/api/featured-products/${productId}/feature`, {
        featured: !currentFeaturedStatus
      });

      // Update local state
      updateProduct(productId, { is_featured: !currentFeaturedStatus });
    } catch (error) {
      console.error('Error toggling featured status:', error);
      alert('שגיאה בעדכון סטטוס המוצר כטרנד');
    }
  };

  // Print inventory report function - fetches ALL products matching filters
  const handlePrintInventoryReport = async () => {
    try {
      // Build query params with current filters but no pagination limit
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category_id', filters.category);
      if (filters.status) params.append('status', filters.status);
      if (filters.minPrice) params.append('min_price', filters.minPrice);
      if (filters.maxPrice) params.append('max_price', filters.maxPrice);
      if (filters.lowStock) params.append('low_stock', 'true');
      params.append('sort', filters.sortBy || 'created_at');
      params.append('order', filters.sortOrder || 'desc');
      params.append('page', '1');
      params.append('limit', '10000'); // Get all products

      // Fetch all products matching filters
      const response = await axios.get(`/api/products?${params.toString()}`);
      const allProducts = response.data.products || [];

      if (allProducts.length === 0) {
        alert('אין מוצרים להדפסה');
        return;
      }

      // Get current filter description
      const getFilterDescription = () => {
        const parts = [];
        if (filters.search) parts.push(`חיפוש: "${filters.search}"`);
        if (filters.status === 'active') parts.push('סטטוס: פעילים');
        if (filters.status === 'inactive') parts.push('סטטוס: לא פעילים');
        if (filters.lowStock) parts.push('מלאי נמוך');
        if (filters.category) {
          const cat = categories.find(c => c.id === filters.category);
          if (cat) parts.push(`קטגוריה: ${cat.name}`);
        }
        return parts.length > 0 ? parts.join(' | ') : 'כל המוצרים';
      };

      // Calculate totals from ALL products
      const totalStock = allProducts.reduce((sum, p) => sum + (p.stock_quantity || 0), 0);
      const totalValue = allProducts.reduce((sum, p) => sum + ((p.stock_quantity || 0) * (p.cost_price || p.price_b2b || 0)), 0);
      const lowStockProducts = allProducts.filter(p => (p.stock_quantity || 0) < 10);

      const printContent = `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>דוח מלאי - ${new Date().toLocaleDateString('he-IL')}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&display=swap');
          
          body {
            font-family: 'Rubik', Arial, sans-serif;
            direction: rtl;
            padding: 20px;
            max-width: 100%;
            margin: 0 auto;
            color: #000;
            font-size: 11px;
          }
          
          .header {
            text-align: center;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          
          .header h1 {
            font-size: 22px;
            font-weight: 700;
            margin: 0 0 5px 0;
          }
          
          .filter-info {
            background: #f3f4f6;
            padding: 8px 12px;
            border-radius: 6px;
            margin-bottom: 15px;
            font-size: 11px;
          }
          
          .summary-cards {
            display: flex;
            gap: 15px;
            margin-bottom: 20px;
          }
          
          .summary-card {
            flex: 1;
            padding: 12px;
            border-radius: 8px;
            text-align: center;
          }
          
          .summary-card.blue { background: #dbeafe; border: 1px solid #93c5fd; }
          .summary-card.green { background: #dcfce7; border: 1px solid #86efac; }
          .summary-card.orange { background: #ffedd5; border: 1px solid #fdba74; }
          .summary-card.purple { background: #f3e8ff; border: 1px solid #c4b5fd; }
          
          .summary-card .value {
            font-size: 20px;
            font-weight: 700;
          }
          
          .summary-card .label {
            font-size: 10px;
            color: #4b5563;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            font-size: 10px;
          }
          
          th {
            background-color: #1e40af;
            color: white;
            padding: 8px 6px;
            text-align: right;
            font-weight: 600;
            font-size: 10px;
          }
          
          td {
            padding: 6px;
            border-bottom: 1px solid #e5e7eb;
            text-align: right;
          }
          
          tr:nth-child(even) {
            background-color: #f9fafb;
          }
          
          tr:hover {
            background-color: #f3f4f6;
          }
          
          .low-stock {
            background-color: #fef2f2 !important;
            color: #991b1b;
          }
          
          .out-of-stock {
            background-color: #fee2e2 !important;
            color: #7f1d1d;
            font-weight: 600;
          }
          
          .stock-ok {
            color: #166534;
          }
          
          .footer {
            margin-top: 20px;
            text-align: center;
            font-size: 10px;
            color: #6b7280;
            border-top: 1px solid #e5e7eb;
            padding-top: 10px;
          }
          
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>📦 דוח מלאי</h1>
          <div style="font-size: 12px; color: #6b7280;">
            תאריך: ${new Date().toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}
          </div>
        </div>

        <div class="filter-info">
          <strong>🔍 פילטרים:</strong> ${getFilterDescription()}
        </div>

        <div class="summary-cards">
          <div class="summary-card blue">
            <div class="value">${allProducts.length}</div>
            <div class="label">מוצרים</div>
          </div>
          <div class="summary-card green">
            <div class="value">${totalStock.toLocaleString()}</div>
            <div class="label">סה"כ יחידות</div>
          </div>
          <div class="summary-card orange">
            <div class="value">${lowStockProducts.length}</div>
            <div class="label">מלאי נמוך</div>
          </div>
          <div class="summary-card purple">
            <div class="value">₪${totalValue.toLocaleString()}</div>
            <div class="label">שווי מלאי</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th style="width: 100px;">מק״ט</th>
              <th>שם המוצר</th>
              <th style="width: 80px;">מלאי</th>
              <th style="width: 80px;">מלאי B2B</th>
              <th style="width: 80px;">מלאי B2C</th>
              <th style="width: 70px;">מחיר</th>
              <th style="width: 60px;">סטטוס</th>
            </tr>
          </thead>
          <tbody>
            ${allProducts.map((product, index) => {
        const stock = product.stock_quantity || 0;
        const stockClass = stock === 0 ? 'out-of-stock' : stock < 10 ? 'low-stock' : '';
        const stockDisplay = stock === 0 ? 'אזל!' : stock < 10 ? `⚠️ ${stock}` : stock;

        return `
                <tr class="${stockClass}">
                  <td style="text-align: center;">${index + 1}</td>
                  <td><code style="font-size: 10px;">${product.sku || '-'}</code></td>
                  <td><strong>${product.name || '-'}</strong></td>
                  <td style="text-align: center; font-weight: 600;">${stockDisplay}</td>
                  <td style="text-align: center;">${product.stock_b2b ?? '-'}</td>
                  <td style="text-align: center;">${product.stock_b2c ?? '-'}</td>
                  <td style="text-align: center;">₪${(product.price_b2b || product.price_b2c || 0).toLocaleString()}</td>
                  <td style="text-align: center;">${product.is_active ? '✅' : '❌'}</td>
                </tr>
              `;
      }).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>דוח מלאי - הופק על ידי מערכת ניהול המוצרים</p>
          <p>סה"כ ${allProducts.length} מוצרים</p>
        </div>
      </body>
      </html>
    `;

      const printWindow = window.open('', '_blank', 'width=1000,height=700');
      if (!printWindow) {
        alert('נא לאפשר חלונות קופצים (popups) בדפדפן כדי להדפיס');
        return;
      }
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    } catch (error) {
      console.error('Print error:', error);
      alert('שגיאה בהדפסה: ' + error.message);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <ProductsHeader
        stats={stats}
        onAdd={() => {
          setAddProductQuickMode(false);
          setShowAddProductModal(true);
        }}
        onQuickAdd={() => {
          setAddProductQuickMode(true);
          setShowAddProductModal(true);
        }}
        onExport={handlePrintInventoryReport}
      />

      <ProductsFilters
        filters={filters}
        categories={categories}
        onFilterChange={updateFilters}
        onClearFilters={clearFilters}
        activeFiltersCount={activeFiltersCount}
      />

      <BulkActionsBar
        selectedCount={bulkActions.selectedCount}
        onActivate={() => bulkActions.executeBulkAction('activate')}
        onDeactivate={() => bulkActions.executeBulkAction('deactivate')}
        onDelete={() => bulkActions.executeBulkAction('delete')}
        onAssignBrand={() => setShowBulkBrandModal(true)}
        onCancel={bulkActions.deselectAll}
        loading={bulkActions.loading}
      />

      <ProductsTable
        products={products}
        categories={categories}
        loading={loading}
        selectedIds={bulkActions.selectedIds}
        onSelectAll={handleSelectAll}
        onSelectProduct={bulkActions.toggleSelect}
        onEditProduct={handleEditProduct}
        onDeleteProduct={handleDeleteProduct}
        onToggleFeatured={handleToggleFeatured}
        inlineEdit={inlineEdit}
      />

      <ProductsPagination
        pagination={pagination}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Modals */}
      <AddProductModern
        isOpen={showAddProductModal}
        onClose={() => {
          setShowAddProductModal(false);
          setAddProductQuickMode(false);
        }}
        onProductAdded={handleProductAdded}
        quickMode={addProductQuickMode}
      />

      <EditProductModern
        isOpen={showEditProductModal}
        onClose={() => {
          setShowEditProductModal(false);
          setEditingProduct(null);
        }}
        product={editingProduct}
        onProductUpdated={handleProductUpdated}
      />

      <DeleteProductConfirmation
        isOpen={showDeleteConfirmModal}
        onClose={() => {
          setShowDeleteConfirmModal(false);
          setDeletingProduct(null);
        }}
        product={deletingProduct}
        onProductDeleted={handleProductDeleted}
      />

      <BulkBrandAssignment
        isOpen={showBulkBrandModal}
        onClose={() => setShowBulkBrandModal(false)}
        selectedProducts={bulkActions.selectedItems}
        onAssignmentComplete={() => {
          bulkActions.deselectAll();
          refetch();
        }}
      />
    </div>
  );
};

export default AdminProducts;
